<script>
import { watch, ref } from 'vue';
import pdfMake from "pdfmake/build/pdfmake";
import { pdfFileName, openPdf } from '@/utils/pdfFile';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import SalesmanShopServices from '@/services/SalesmanShopServices';
import moment from 'moment';
export default {
  props: {
    day: String,
    salesman_id: String,
    salesman_name: String,
    counter: Number,
  },
  setup(props) {
    const root = ref(null);
    const pdfData = ref([]);
    const pdfDate = ref('');
    const documentDefinition = ref({});
    const body = ref([]);
    const totalBalance = ref(0);

    // -------- Helper Functions --------
    const formatDay = (day) => (day ? day.charAt(0).toUpperCase() + day.slice(1) : '');

    const safeText = (value) =>
      value === null || value === undefined ? '' : String(value);

    const extractAmount = (value) => {
      const numericValue = parseFloat(value);
      return Number.isNaN(numericValue) ? null : numericValue;
    };

    const formatAmount = (value) => {
      const numericValue = extractAmount(value);
      return numericValue === null ? '' : numericValue.toFixed(2);
    };

    const buildDateAmountCell = (dateValue, amountValue) => {
      const dateText = safeText(dateValue);
      const amountText = formatAmount(amountValue);

      if (!dateText && !amountText)
        return { text: '', style: 'tableCell', alignment: 'center' };

      const stack = [];
      if (dateText) stack.push({ text: dateText });
      if (amountText) stack.push({ text: `(${amountText})` });

      return { stack, style: 'tableCell', alignment: 'center' };
    };

    const createHeaderCell = (text) => ({
      text,
      style: ['tableHeader'],
      fillColor: '#f3f3f3',
    });

    // -------- Main PDF Generation --------
    const loadPdf = async () => {
      try {
        // 1️⃣ Determine target date
        const daysMap = {
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
          sunday: 7,
        };
        const day_number = daysMap[props.day?.toLowerCase()] || 1;
        const current_day = moment().isoWeekday();

        pdfDate.value =
          current_day === day_number
            ? moment().format('DD-MM-YYYY')
            : current_day < day_number
            ? moment().isoWeekday(day_number).format('DD-MM-YYYY')
            : moment().add(1, 'weeks').isoWeekday(day_number).format('DD-MM-YYYY');

        // 2️⃣ Fetch data
        const response = await SalesmanShopServices.getDailyPdfContent(
          props.salesman_id,
          props.day
        );
        pdfData.value = response.data.data || [];

        // 3️⃣ Build table
        body.value = [];
        totalBalance.value = 0;

        const columns = [
          createHeaderCell('Sr#'),
          createHeaderCell('Shop Name'),
          createHeaderCell('Address'),
          createHeaderCell('Last Bill Date & Amount'),
          createHeaderCell('Last Recovery Date & Amount'),
          createHeaderCell('Current Balance'),
          createHeaderCell('Received'),
        ];
        body.value.push(columns);

        let i = 1;
        for (const row of pdfData.value) {
          const dataRow = [];

          dataRow.push({ text: i.toString(), style: 'tableCell', alignment: 'center' });
          dataRow.push({ text: safeText(row.shop_name), style: 'tableCell', alignment: 'center' });
          dataRow.push({ text: safeText(row.address), style: 'tableCell', alignment: 'center' });
          dataRow.push(buildDateAmountCell(row.lastBillDate, row.debitAmount));
          dataRow.push(buildDateAmountCell(row.lastRecoveryDate, row.creditAmount));

          const formattedBalance = formatAmount(row.currentBalance);
          dataRow.push({ text: formattedBalance, style: 'tableCell', alignment: 'center' });

          // ✅ Add empty "Received" column
          dataRow.push({ text: '', style: 'tableCell', alignment: 'center' });

          const numericBalance = extractAmount(row.currentBalance);
          if (numericBalance !== null) totalBalance.value += numericBalance;

          body.value.push(dataRow);
          i++;
        }

        // 4️⃣ Add Total Row
        body.value.push([
          { text: 'Total', colSpan: 5, style: ['tableTotal', 'textRight'], margin: [0, 0, 6, 6] },
          {}, {}, {}, {},
          { text: totalBalance.value.toFixed(2), style: ['tableTotal', 'textCenter'], margin: [0, 0, 0, 6] },
          { text: '', style: 'tableCell', alignment: 'center' }, // keep columns consistent
        ]);

        // 5️⃣ Create document definition
        documentDefinition.value = {
          pageMargins: [30, 36, 30, 60],
          content: [
            {
              columns: [
                {
                  width: '*',
                  text: `Salesman: ${props.salesman_name} (${props.salesman_id})`,
                  style: ['reportMeta', 'mb2'],
                },
                {
                  width: 'auto',
                  stack: [
                    { text: `Day: ${formatDay(props.day)}`, alignment: 'right' },
                    { text: `Date: ${pdfDate.value}`, alignment: 'right' },
                  ],
                  style: ['reportMeta', 'mb2'],
                },
              ],
              margin: [0, 0, 0, 6],
            },
            {
              table: {
                headerRows: 1,
                widths: [28, '*', '*', '*', '*', '*', 65],
                body: body.value,
              },
              layout: {
                hLineWidth: () => 0.7,
                vLineWidth: () => 0.7,
                hLineColor: '#000000',
                vLineColor: '#000000',
                paddingTop: () => 5,
                paddingBottom: () => 5,
              },
            },
          ],
          styles: {
            reportMeta: { fontSize: 12, bold: true },
            tableHeader: {
              fontSize: 12,
              bold: true,
              alignment: 'center',
              margin: [0, 4, 0, 4],
              fillColor: '#f3f3f3',
            },
            tableCell: { fontSize: 11, bold: true },
            tableTotal: { fontSize: 12, bold: true },
            textRight: { alignment: 'right' },
            textCenter: { alignment: 'center' },
            mb2: { margin: [0, 0, 0, 4] },
          },
        };

        // 6️⃣ Generate PDF
        pdfMake.vfs = pdfFonts;
        openPdf(pdfMake, documentDefinition.value, pdfFileName(['Salesman-Shops', props.salesman_name, props.day], ''));
      } catch (error) {
        console.error('❌ Error generating PDF:', error);
      }
    };

    // -------- Watchers --------
    watch(
      [() => props.counter, () => props.day, () => props.salesman_id],
      () => loadPdf()
    );

    return { root, pdfData, pdfDate, documentDefinition, body, totalBalance };
  },
};
</script>
<style>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap');

* {
  box-sizing: border-box;
  font-family: 'Roboto', sans-serif;
}

.pdf-wrapper {
  margin: 40px 30px;
}

.text-black {
  color: #000;
}

.w-100 {
  width: 100%;
}

.float-left {
  float: left;
}

.float-right {
  float: right;
}

.font-weight-bold {
  font-weight: 600;
}

.font-13 {
  font-size: 13px;
}

.text-upper {
  text-transform: uppercase;
}

.text-capitalized {
  text-transform: capitalize;
}

.dis-inline-block {
  display: inline-block;
}

.v-align-middle {
  vertical-align: middle;
}

.clearfixed:after {
  content: '';
  display: table;
  clear: both;
}

table {
  border-collapse: collapse;
  line-height: 14px;
}

.border-table,
.border-table th,
.border-table td {
  border: 2px solid #000;
}

.border-table th,
.border-table td {
  padding: 4px 6px;
  min-width: 110px;
  max-width: 120px;
}

.border-table th.small-size,
.border-table td.small-size {
  min-width: 50px;
  max-width: 50px;
}

.text-align-center {
  text-align: center;
}

.text-align-right {
  text-align: right;
}

.p-0 {
  padding: 0;
}

.m-0 {
  margin: 0;
}
</style>
